"use client";

import { useEffect, useRef } from "react";
import { useMessages, useTranslations } from "next-intl";
import Link from "next/link";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import HeroNewsFeed, { type NewsHeadlineItem } from "./HeroNewsFeed";
import {
  MailIcon,
  PhoneIcon,
  TelegramIcon,
  WhatsAppIcon,
  ContactsIcon,
  ChatIcon,
} from "./ContactIcons";
import { CONTACTS } from "@/lib/site-contacts";

type MessageTree = Record<string, Record<string, string>>;

export default function Hero({
  newsHeadlines,
  newsHasMore,
}: {
  newsHeadlines?: NewsHeadlineItem[];
  newsHasMore?: boolean;
} = {}) {
  const t = useTranslations("Hero");
  const messages = useMessages() as MessageTree;
  const heroMessages = messages.Hero ?? {};
  const locale = useLocale();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;
    el.setAttribute("muted", "");
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");

    const isMobile = window.innerWidth <= 767;
    const nextSrc = isMobile
      ? "/videos/hero-mobile.mp4?v=4"
      : "/videos/hero.mp4?v=4";

    const tryPlay = () => {
      const p = el.play();
      if (p) p.catch((error: unknown) => {
        // Autoplay blocked until user gesture (NotAllowedError) is expected on
        // Safari/iOS; the poster shows meanwhile and gestures retry playback.
        if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError")) {
          return;
        }
        console.error("Hero video play failed:", error);
      });
    };

    let started = false;
    // Defer video download until after window load + idle so the preloaded
    // poster (LCP element) is not competing with the video for bandwidth.
    const startPlayback = () => {
      if (started) return;
      started = true;
      if (el.getAttribute("src") !== nextSrc) {
        el.setAttribute("src", nextSrc);
        el.load();
      }
      tryPlay();
      el.addEventListener("loadedmetadata", tryPlay);
      el.addEventListener("loadeddata", tryPlay);
      el.addEventListener("canplay", tryPlay);
      el.addEventListener("canplaythrough", tryPlay);
    };

    let idleHandle: number | undefined;
    const scheduleStart = () => {
      if (started) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(startPlayback, { timeout: 2500 });
      } else {
        idleHandle = window.setTimeout(startPlayback, 1200);
      }
    };

    if (document.readyState === "complete") {
      scheduleStart();
    } else {
      window.addEventListener("load", scheduleStart, { once: true });
    }

    const onGesture = () => {
      startPlayback();
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("scroll", onGesture);
    };
    window.addEventListener("touchstart", onGesture);
    window.addEventListener("scroll", onGesture);
    return () => {
      if (idleHandle !== undefined) {
        if (typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleHandle);
        } else {
          window.clearTimeout(idleHandle);
        }
      }
      el.removeEventListener("loadedmetadata", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("canplaythrough", tryPlay);
      window.removeEventListener("load", scheduleStart);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("scroll", onGesture);
    };
  }, []);

  const safeT = (key: string, fallback = key) => {
    if (typeof heroMessages[key] === "string") {
      return t(key as never);
    }
    return fallback;
  };

  return (
    <section className="pager-hero relative z-10 flex w-screen items-center overflow-hidden min-h-screen bg-[#202e64] max-md:min-h-svh max-md:w-full md:-ml-[calc(10vw_+_25px)]">
      {/* Видео-фон на весь hero */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        poster="/videos/hero-poster.webp?v=4"
        className="absolute inset-0 h-full w-full object-cover bg-[#202e64]"
      >
      </video>

      {/* Затемнение для читаемости текста и иконок */}
      <div className="pointer-events-none absolute inset-0 bg-black/50" />

      {/* Горизонтальные линии — привязаны к видео, скроллятся с ним */}
      <div className="dev48-top-line" aria-hidden="true" />
      <div className="dev48-bottom-line" aria-hidden="true" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 pb-6 pt-[calc(theme(spacing.14)+env(safe-area-inset-top))] md:pb-12 max-md:pt-[calc(3.5rem+env(safe-area-inset-top))] md:pl-[calc(10vw_+_25px)] md:pr-[calc(10vw_+_25px)] lg:py-28">
        <div className="flex flex-col gap-6 md:gap-10 lg:flex-row lg:items-center lg:gap-12 xl:gap-16">
        <div className="max-w-2xl flex-1">
              <h1
                className={
                  "font-bold text-white leading-[1.1] tracking-tight text-left lg:mt-0 " +
                  (locale === "ru"
                    ? "text-5xl lg:text-6xl"
                    : "text-4xl sm:text-5xl lg:text-6xl")
                }
              >
              {safeT("heroTitle", "Our Innovation, Your Path to Success")}
            </h1>

            <p className="text-sm text-white/60 text-left mt-10 max-md:mt-6">
              {safeT("heroSubtitle", "We specialize in providing full-cycle support")}
            </p>

            <div className="flex flex-wrap gap-3 justify-start items-center mt-10 max-md:mt-6">
              <a href={`mailto:${CONTACTS.email}`} className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200" title={CONTACTS.email}>
                <MailIcon />
              </a>
              <a href={`tel:${CONTACTS.phone}`} className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200" title={CONTACTS.phoneDisplay}>
                <PhoneIcon />
              </a>
              <a href={CONTACTS.whatsappUrl} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200" title={`WhatsApp ${CONTACTS.whatsappDisplay}`}>
                <WhatsAppIcon />
              </a>
              <a href={CONTACTS.telegramUrl} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200" title={CONTACTS.telegramHandle}>
                <TelegramIcon />
              </a>
              <Link href={getPathWithLocale("/contacts", locale)} className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200" title="Контакты">
                <ContactsIcon />
              </Link>
              <Link
                href={getPathWithLocale("/chat", locale)}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                title={locale.startsWith("en") ? "Discuss in chat" : "Обсудить в чате"}
              >
                <ChatIcon />
              </Link>
            </div>
          </div>

          <HeroNewsFeed
            initialItems={newsHeadlines ?? []}
            initialHasMore={Boolean(newsHasMore)}
            label={safeT("newsTitle", "НОВОСТИ")}
            allNewsLabel={safeT("allNews", "Все новости")}
          />
        </div>
        </div>
    </section>
  );
}
